// @flow
import { has } from "../../utils/helpers/objects";

import ProcessStatusSettingsModel from "../process/ProcessStatusSettingsModel";
import AttributeCollection from "../attributes/AttributeCollection";
import BaseModel from "../base/BaseModel";
import Href from "../href/Href";

import {
  HTTP_METHODS,
  CREATE_ACTION,
  UPDATE_ACTION,
  DELETE_ACTION,
} from "../../constants";

import type { AttributeType, ModelOptions } from "../types";

/**
 * Defines an Action. For instance an action on the tab 'books', which leads to a form
 */
export default class ActionModel extends BaseModel {
  _fieldCollection: AttributeCollection;
  _href: Href;
  _icon: string;

  constructor(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(data, contributions, modelOptions);

    this._fieldCollection = new AttributeCollection(
      this.data.fields,
      this.contributions.fields,
      { ...modelOptions, isReadonly: false },
    );
  }

  /**
   */
  static createFromHref(
    name: string,
    href: string,
    label: string,
    type: string = "form",
    origin?: string,
    contextPath?: string,
  ): ActionModel {
    const data = { name, href };
    const contributions = { name, label: label ?? name, type };
    return new ActionModel(data, contributions, { origin, contextPath });
  }

  /**
   */
  get isDisabled(): boolean {
    return this.data.href == null;
  }

  /**
   * retrieve href of action
   */
  get selfhref(): Href {
    return (
      this._href ??
      new Href(
        `${this.data.href}?${this.querystring}`,
        "Form",
        this.modelOptions,
      )
    );
  }

  /**
   */
  set selfhref(href: Href) {
    this._href = href;
  }

  /**
   * retrieve request method
   */
  get method(): $Keys<typeof HTTP_METHODS> {
    return this.getData("method", HTTP_METHODS.POST);
  }

  /**
   * Retrieve type of method
   */
  get type(): string {
    if (this.layouthint.has(CREATE_ACTION)) {
      return "create";
    }
    if (this.layouthint.has(UPDATE_ACTION)) {
      return "update";
    }
    if (this.layouthint.has(DELETE_ACTION)) {
      return "delete";
    }

    return this.getContribution("type", "general");
  }

  /**
   * Retrieve name of action
   */
  get name(): string {
    return this.getData("name", "unknown");
  }

  /**
   * retrieve the collection of field attributes as an array
   */
  get fields(): Array<AttributeType> {
    return this._fieldCollection.all;
  }

  /**
   * Retrieve the field collection
   */
  get fieldCollection(): AttributeCollection {
    return this._fieldCollection;
  }

  /**
   */
  set fieldCollection(fieldCollection: AttributeCollection) {
    this._fieldCollection = fieldCollection;
  }

  /**
   * Retrieve querystring of action
   */
  get querystring(): string {
    return this.fields
      .filter((attribute) => attribute.value != null && attribute.value !== "")
      .map((attribute) => {
        const { value } = attribute;
        if (value !== null) {
          return `${attribute.name}=${value}`;
        }
        return attribute.name;
      })
      .join("&");
  }

  /**
   * Retrieve a field by it's key
   */
  getFieldByKey(key: string): AttributeType | null {
    return this._fieldCollection.getAttributeByKey(key);
  }

  /**
   * Indicates if field exists by the given key
   */
  hasFieldByKey(key: string): boolean {
    return this.getFieldByKey(key) !== null;
  }

  /**
   * Getting the key/name of this action
   */
  get key(): string {
    return this.data.name;
  }

  /**
   * Getting the label of the action
   */
  get label(): string {
    return this.getContribution("label", this.key);
  }

  /**
   */
  get isProcessTask(): boolean {
    return has(this.contributions, "processTask");
  }

  /**
   */
  get processStatus(): ProcessStatusSettingsModel | null {
    if (this.isProcessTask) {
      return new ProcessStatusSettingsModel(
        this.getData("processStatus", {}),
        this.getContribution("processTask", {}),
        this.modelOptions,
      );
    }

    return null;
  }

  /**
   */
  get icon(): string {
    return this._icon;
  }

  /**
   */
  set icon(icon: string) {
    this._icon = icon;
  }
}
